import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleAxiosError } from '../../axios/handleAxiosError';
import { handleError } from '../../utils/handleError';
import { ODataParams } from '../_ODataTypes';
import { OrderInitials, CoreOrderResponseListResponse, GetOrderResponse, CoreDocumentResponseListResponse } from './_orderTypes';
import { buildApiUrl } from '../../axios/urlCreator';
import { cachedGet } from '../../axios/responseCache';

const initialState: OrderInitials = {
    orderList: null,
    orderDetail: null,
    orderDocuments: null,
    loading: false,
    error: null,
};

const orderSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {

    },
    extraReducers: (builder) => {
        builder
            // GET Order List
            .addCase(getOrderList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getOrderList.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.orderList = action.payload;
            })
            .addCase(getOrderList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Order Detail
            .addCase(getOrderDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getOrderDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.orderDetail = action.payload;
            })
            .addCase(getOrderDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Order Documents
            .addCase(getOrderDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getOrderDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.orderDocuments = action.payload;
            })
            .addCase(getOrderDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

// GET Order List
export const getOrderList = createAsyncThunk<CoreOrderResponseListResponse, ODataParams | void>(
    'orders/getOrderList',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.filter) queryParams.append('$filter', params.filter);
            if (params?.orderby) queryParams.append('$orderby', params.orderby);
            if (params?.top != null) queryParams.append('$top', params.top.toString());
            if (params?.skip != null) queryParams.append('$skip', params.skip.toString());

            return await cachedGet<CoreOrderResponseListResponse>(buildApiUrl("orders", queryParams), 15_000);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);


// GET Order Detail
export const getOrderDetail = createAsyncThunk<GetOrderResponse, string>(
    'orders/getOrderDetail',
    async (orderNumber, { rejectWithValue }) => {
        try {
            return await cachedGet<GetOrderResponse>(buildApiUrl(`orders/${encodeURIComponent(orderNumber)}`), 15_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Order Documents
export const getOrderDocuments = createAsyncThunk<CoreDocumentResponseListResponse, string>(
    'orders/getOrderDocuments',
    async (orderNumber, { rejectWithValue }) => {
        try {
            return await cachedGet<CoreDocumentResponseListResponse>(buildApiUrl(`orders/${encodeURIComponent(orderNumber)}/documents`), 15_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);


export default orderSlice.reducer;
